import { useContext } from 'react';
import pluralize from 'pluralize';

import { media } from 'context';
import { Layout, ProductImg, Text } from 'ds';
import AddQuantity from 'ds/AddQuantity';
import TextButton from 'ds/TextButton';
import { formatUsd } from 'helpers';
import { ConsumablesProduct } from 'shared';

interface ConsumablesProductQuantityCardProps {
  product: ConsumablesProduct;
  quantity: number;
  onQuantityChange?: (quantity: number) => void;
  isSaved?: boolean;
  removeItem?: () => void;
  showQuantitySelector?: boolean;
  vendorTask?: boolean;
  showSkipRemoveItemButton?: boolean;
  skipRemoveItemText?: string;
  lockedAmountCents?: number | null;
}
const ConsumablesProductQuantityCard = ({
  product,
  quantity,
  onQuantityChange,
  isSaved,
  removeItem,
  showQuantitySelector = true,
  vendorTask = false,
  showSkipRemoveItemButton = false,
  skipRemoveItemText = 'Skip item',
  lockedAmountCents
}: ConsumablesProductQuantityCardProps) => {
  const { isMobile } = useContext(media);
  const { amount_cents, unit_type, units_per_package, package_type, brand, detail } = product;
  const formattedAmount = formatUsd(lockedAmountCents != null ? lockedAmountCents / 100 : amount_cents / 100);
  const totalPrice = quantity * (lockedAmountCents != null ? lockedAmountCents : amount_cents);
  return (
    <Layout direction="column" gap={12}>
      <Layout flex direction="row" width="100%" align={isMobile ? 'flex-start' : 'center'}>
        <ProductImg
          srcKey={product.image_key}
          size="sm"
          alt={detail}
          style={{
            width: isMobile ? 80 : 72,
            height: isMobile ? 80 : 72,
            marginRight: 16,
            opacity: showQuantitySelector && quantity === 0 ? 0.5 : 1
          }}
        />
        <Layout
          flex
          direction="column"
          flexGrow={1}
          {...(isMobile ? { height: '100%' } : { height: vendorTask ? 48 : undefined, width: '100%' })}
          gap={8}
        >
          <Layout direction="row">
            <Text size="body-sm" color={showQuantitySelector && quantity === 0 ? 'gray-400' : undefined}>
              {brand} {detail} {pluralize(unit_type, 2)} ({units_per_package}/{package_type})
            </Text>
            {vendorTask ? (
              <Layout paddingX={12} height={48} marginLeft={12} flexGrow={1} justify="flex-end">
                <Text size="body-sm" semibold>
                  {quantity}
                </Text>
              </Layout>
            ) : showQuantitySelector && quantity === 0 ? null : (
              <Layout
                {...(isMobile ? { marginLeft: 26 } : { marginLeft: 16, flexGrow: 1 })}
                direction="column"
                height="100%"
                align="flex-end"
              >
                <Text size="body-sm" color="gray-900" semibold align="right">
                  {formatUsd(totalPrice / 100)}
                </Text>
                <Text italic size="body-xs" color="gray-700" wrap={false} align="right">
                  {formattedAmount} ea.
                </Text>
              </Layout>
            )}
          </Layout>
          {(vendorTask || !showQuantitySelector) && (
            <Layout flex direction="row">
              {vendorTask ? (
                <Text size="body-sm" color="gray-400">
                  SKU: {product.vendor_sku}
                </Text>
              ) : (
                <>
                  {!showQuantitySelector && (
                    <Text size="body-sm" color="gray-700">
                      Qty: {quantity}
                    </Text>
                  )}
                </>
              )}
            </Layout>
          )}
          {showQuantitySelector && (
            <Layout>
              {quantity > 0 ? (
                <Layout direction="row" gap={16} align="center" width="fit-content">
                  <Layout width={124} minWidth={124}>
                    <AddQuantity
                      value={quantity}
                      showRemoveItemButton={quantity === 1}
                      minValue={1}
                      handleDecreaseQuantity={() => {
                        onQuantityChange?.(quantity - 1);
                      }}
                      handleIncreaseQuantity={() => {
                        onQuantityChange?.(quantity + 1);
                      }}
                      handleRemoveItem={() => {
                        // if the item has been saved, we add a key to the item called itemRemoved
                        if (isSaved) {
                          onQuantityChange?.(0);
                        } else {
                          removeItem?.();
                        }
                      }}
                      textAndIconColor="gray-900"
                      size="sm"
                    />
                  </Layout>
                  {showSkipRemoveItemButton && (
                    <TextButton
                      text={skipRemoveItemText}
                      color="gray-400"
                      size="xs"
                      semibold={false}
                      textSize="body-sm"
                      onClick={() => {
                        onQuantityChange?.(0);
                      }}
                    />
                  )}
                </Layout>
              ) : (
                <Layout direction="row" gap={16}>
                  <TextButton
                    text="Add back item"
                    color="blue-500"
                    size="xs"
                    semibold={false}
                    textSize="body-sm"
                    onClick={() => {
                      onQuantityChange?.(1);
                    }}
                  />
                  <Text size="body-sm" color="gray-400">
                    {formattedAmount} ea.
                  </Text>
                </Layout>
              )}
            </Layout>
          )}
        </Layout>
      </Layout>
    </Layout>
  );
};

export default ConsumablesProductQuantityCard;
